/**
 * Pure geometry for the zoomable lightbox stage.
 *
 * Kept free of any DOM / React so the tricky anchor-zoom and edge-clamp
 * maths can be unit-tested deterministically — jsdom has no layout engine
 * (naturalWidth / getBoundingClientRect are all 0), so this is the only
 * place the zoom behaviour can be verified without a real browser.
 *
 * Coordinate model: the stage `<img>` is positioned with
 * `transform: translate(tx, ty) scale(scale)` and `transform-origin: 0 0`.
 * A point at image-space offset `p` therefore lands at viewport coord
 * `t + p * scale`.
 */

/** Wheel zoom factor per notch. */
export const WHEEL_FACTOR = 1.12
/** Double-tap / double-click toggles fit ↔ fit × this factor. */
export const DOUBLE_TAP_SCALE_FACTOR = 2.6
/** Hard upper bound on zoom (lower bound is always the computed fit scale). */
export const MAX_SCALE = 6
/** Padding around the fitted image on small / large viewports. */
export const FIT_PAD_SMALL = 16
export const FIT_PAD_LARGE = 56
/** Viewport width below which the small pad applies. */
export const SMALL_VIEWPORT = 600
/** Max gap (ms) and movement (px) between two pointerups to count as a double-tap. */
export const DOUBLE_TAP_MS = 300
export const DOUBLE_TAP_MOVE = 6

export interface Vec2 {
  x: number
  y: number
}

export interface Transform {
  scale: number
  tx: number
  ty: number
}

/** Clamp `v` into the inclusive `[min, max]` range. */
export function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max)
}

/**
 * Scale that shows the whole image centred with breathing room, never
 * upscaling past the natural pixel size. This is also the minimum scale.
 */
export function computeFitScale(args: {
  natW: number
  natH: number
  vw: number
  vh: number
}): number {
  const { natW, natH, vw, vh } = args
  if (natW <= 0 || natH <= 0) return 1 // dimensions unknown yet — safe default
  const pad = vw < SMALL_VIEWPORT ? FIT_PAD_SMALL : FIT_PAD_LARGE
  const usableW = vw - pad
  const usableH = vh - pad
  // Degenerate / tiny viewport (≤ pad on either axis): no positive fit exists.
  // Default to natural size (downstream clamp re-centers) rather than a 0 or
  // negative scale, which would poison every scale-dividing call.
  if (usableW <= 0 || usableH <= 0) return 1
  const fit = Math.min(usableW / natW, usableH / natH)
  return Math.min(fit, 1)
}

/**
 * Zoom from `scale` to `next` while keeping the viewport point `(cx, cy)`
 * pinned to the same pixel of the image (cursor / pinch-centre anchored).
 */
export function applyAnchorZoom(args: {
  scale: number
  tx: number
  ty: number
  next: number
  cx: number
  cy: number
}): Transform {
  const { scale, tx, ty, next, cx, cy } = args
  const k = next / scale
  return {
    scale: next,
    tx: cx - (cx - tx) * k,
    ty: cy - (cy - ty) * k,
  }
}

/**
 * One pinch frame: scale by the finger-distance ratio while keeping the image
 * content under the PREVIOUS midpoint pinned to the CURRENT midpoint, so the
 * picture sticks to the fingers as they spread/pan.
 *
 * Decomposition: anchor the zoom at `lastMid` (keeps the pixel under it fixed
 * across the scale change), then translate by (mid - lastMid). Anchoring at the
 * live `mid` instead would double-count the finger travel and the content would
 * only follow the fingers fractionally.
 */
export function applyPinch(args: {
  scale: number
  tx: number
  ty: number
  lastDist: number
  dist: number
  lastMid: Vec2
  mid: Vec2
  fit: number
}): Transform {
  const { scale, tx, ty, lastDist, dist, lastMid, mid, fit } = args
  const ratio = lastDist > 0 ? dist / lastDist : 1
  const next = clamp(scale * ratio, fit, MAX_SCALE)
  const zoomed = applyAnchorZoom({ scale, tx, ty, next, cx: lastMid.x, cy: lastMid.y })
  return {
    scale: next,
    tx: zoomed.tx + (mid.x - lastMid.x),
    ty: zoomed.ty + (mid.y - lastMid.y),
  }
}

/**
 * Constrain the translation so the image cannot be dragged past its own
 * edges. On any axis where the scaled image is smaller than the viewport
 * it is centred; otherwise it is clamped to keep the viewport covered.
 */
export function clampTranslate(args: {
  tx: number
  ty: number
  scale: number
  natW: number
  natH: number
  vw: number
  vh: number
}): { tx: number; ty: number } {
  const { tx, ty, scale, natW, natH, vw, vh } = args
  const w = natW * scale
  const h = natH * scale
  return {
    tx: w <= vw ? (vw - w) / 2 : clamp(tx, vw - w, 0),
    ty: h <= vh ? (vh - h) / 2 : clamp(ty, vh - h, 0),
  }
}

/** Euclidean distance between two points. */
export function distance(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(bx - ax, by - ay)
}

/** Midpoint between two points. */
export function midpoint(ax: number, ay: number, bx: number, by: number): Vec2 {
  return { x: (ax + bx) / 2, y: (ay + by) / 2 }
}
